type APN = {
  address: string;
  user: string;
  password?: string;
};

type IP = {
  ip: string;
  port: number;
};

type DNS = {
  address: string;
  port: number;
};

type Locktype = number;

type LocktypeVariation = number;

type DataTransmission = number;

type Odometer = number;

type KeepAlive = number;

type AccelerometerSensitivity = number;

type SensitivityAdjustment = number;

type LBSPosition = boolean;

type CorneringPositionUpdate = boolean;

type Led = boolean;

type VirtualIgnition = boolean;

type VirtualIgnitionByVoltage = boolean;

type VirtualIgnitionByMovement = boolean;

type MaxSpeed = number;

type CommunicationType = 'TCP' | 'UDP';

type ProtocolType = 'E3+' | 'GT06';

type AntiTheft = boolean;

type JammerDetection = boolean;

type AngleAdjustment = number;

type LockTypeProgression = {
  n1: number;
  n2: number;
};

type IgnitionByVoltage = {
  initial: number;
  final: number;
};

interface Check extends Object {
  apn?: APN;
  lock_type?: Locktype;
  data_transmission_on?: DataTransmission;
  data_transmission_off?: DataTransmission;
  odometer?: Odometer;
  keep_alive?: KeepAlive;
  accelerometer_sensitivity?: AccelerometerSensitivity;
  lbs_position?: LBSPosition;
  led?: Led;
  max_speed?: number;
  sensitivity_adjustment?: number;
  communication_type?: CommunicationType;
  protocol_type?: ProtocolType;
  anti_theft?: AntiTheft;
  jammer_detection?: JammerDetection;
  angle_adjustment?: AngleAdjustment;
  input_1?: number;
  input_2?: number;
  ignition_status_hb?: JammerDetection;
  economy_mode?: number;
  lock_type_progression?: { n1: number; n2: number };
}

interface Status {
  [key: string]: string;
}

export namespace Bws4gLora {
  export interface AutoTest {
    DEV: string; //DM_BWS_PLUS_4G_LORA,
    SN: string; //99101210,
    IC: string; //89555480000070821034,
    IM: string; //860710052284208,
    FW: string; //BWSiot_E3+4G_SC_V0.0.34 (MASA) (Jun 30 2025 14:11:23),
    GPS: string; //OK,
    GPSf: string; //NOK,
    MDM: string; //OK,
    RSI: string; //OK,
    IN1: string; //NOK,
    OUT: string; //NOK,
    ACELC: string; //STK8321,
    ACELP: string; //OK,
    ID_ACEL: string; //35,
    ID_MEM: string; //000,
    VCC: string; //1353,
    BATT_VOLT: string; //415,
    CHARGER: string; //OK,
    TEMP: string; //25
  }
}

export class Bws4gLoraParser {
  static check(input: string): Check | undefined {
    if (typeof input !== 'string') return undefined;

    // O CHECK do BWS 4G começa com "Sim=" e sempre traz marcadores fixos.
    // Sem esses marcadores, descarta — evita falsos positivos vindo do dump verboso
    // de STATUS (--MODEM--, --GPS--) que também tem pares chave=valor.
    const hasCheckSignature =
      input.includes('Sim=') && input.includes('PROT=') && input.includes('PROT_COM=');
    if (!hasCheckSignature) return undefined;

    // Em respostas de batch, o CHECK vem concatenado ao STATUS por ';' na mesma linha
    // ("...ISHB=ON;BATTERY EXTERNAL:11.84V;..."). O CHECK do BWS 4G nunca usa ';' internamente
    // (separadores são espaço e vírgula), então cortamos no primeiro ';' para isolar o bloco do
    // CHECK. Sem isso, o último campo (ISHB) seria parseado com o STATUS colado ("ON;BATTERY...").
    const checkBlock = input.split(';')[0];

    const parsed: Check = {};
    const obj: Record<string, string> = {};

    // Abordagem melhorada: split por espaço, mas depois remonta pares com espaços
    // Alguns valores contêm espaços (ex: OUT_MODE=PROG DIR, APN=valor,valor,valor)
    const tokens = checkBlock.split(/\s+/);

    // Reconstruir pares chave=valor, juntando tokens quando necessário
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      if (!token) continue;

      // Verifica se é um novo par chave=valor
      if (token.includes('=')) {
        const [key, ...valueParts] = token.split('=');
        let value = valueParts.join('=');

        // Verifica os próximos tokens para ver se são parte do valor (não são chave=)
        // Um token é parte do valor se não contém '=' ou se está continuando um valor anterior
        let j = i + 1;
        while (j < tokens.length && !tokens[j].includes('=')) {
          value += ` ${tokens[j]}`;
          j++;
        }

        // Pula os tokens que foram consumidos como parte do valor
        i = j - 1;

        if (key && value !== undefined) {
          obj[key.trim()] = value.trim();
        }
      }
    }

    if (Object.keys(obj).length === 0) return undefined;

    Object.entries(obj).forEach((entrie) => {
      const [key, value] = entrie;
      if (key === 'APN') {
        parsed.apn = Bws4gLoraParser.apn(value);
      }
      if (key === 'OUT_MODE') {
        parsed.lock_type = Bws4gLoraParser.lock_type(value);
      }
      if (key === 'HB') {
        parsed.data_transmission_on = Bws4gLoraParser.data_transmission_on(value);
        parsed.data_transmission_off = Bws4gLoraParser.data_transmission_off(value);
      }
      if (key === 'DK') {
        parsed.odometer = Bws4gLoraParser.odometer(value);
      }
      if (key === 'TX') {
        parsed.keep_alive = Bws4gLoraParser.keep_alive(value);
      }
      if (key === 'ZD') {
        parsed.accelerometer_sensitivity = Bws4gLoraParser.accelerometer_sensitivity(value);
      }
      if (key === 'LBS') {
        parsed.lbs_position = Bws4gLoraParser.lbs_position(value);
      }
      if (key === 'LED') {
        parsed.led = Bws4gLoraParser.led(value);
      }

      if (key === 'OD') {
        parsed.max_speed = Bws4gLoraParser.max_speed(value);
      }

      if (key === 'PROT_COM') {
        parsed.communication_type = Bws4gLoraParser.communication_type(value);
      }

      if (key === 'PROT') {
        parsed.protocol_type = Bws4gLoraParser.protocol_type(value);
      }

      if (key === 'AF') {
        parsed.anti_theft = Bws4gLoraParser.anti_theft(value);
      }
      if (key === 'JD') {
        parsed.jammer_detection = Bws4gLoraParser.jammer_detection(value);
      }
      if (key === 'TDET') {
        parsed.angle_adjustment = Bws4gLoraParser.angle_adjustment(value);
      }
      if (key === 'IN1_MODE') {
        parsed.input_1 = Bws4gLoraParser.input_1(value);
      }
      if (key === 'IN2_MODE') {
        parsed.input_2 = Bws4gLoraParser.input_2(value);
      }
      if (key === 'GS') {
        parsed.sensitivity_adjustment = Bws4gLoraParser.sensitivity_adjustment(value);
      }
      if (key === 'ISHB') {
        parsed.ignition_status_hb = Bws4gLoraParser.ignition_status_hb(value);
      }
      if (key === 'SDMS') {
        parsed.economy_mode = Bws4gLoraParser.economy_mode(value);
      }
      // DC=n1,n2 — progressive lock timing (ms)
      if (key === 'DC') {
        const [n1, n2] = value.split(',');
        if (n1 && n2 && !Number.isNaN(Number(n1)) && !Number.isNaN(Number(n2))) {
          parsed.lock_type_progression = { n1: Number(n1), n2: Number(n2) };
        }
      }
    });

    if (Object.keys(parsed).length === 0) return undefined;
    return parsed;
  }

  static status(input: string) {
    if (typeof input !== 'string') return undefined;
    // STATUS response always contains these markers — reject boot/firmware lines early
    if (!input.includes('BATTERY EXTERNAL') && !input.includes('ACC:')) return undefined;
    const obj: Status = {};
    const keyValuePairs = input.split(';');

    keyValuePairs.forEach((pair) => {
      let key: string | undefined;
      let value: string | undefined;

      if (pair.includes(':')) {
        [key, value] = pair.split(':');
      }

      if (key && value !== undefined) {
        obj[key.trim()] = value.trim();
      }
    });

    if (Object.keys(obj).length === 0) return undefined;

    return obj;
  }

  static serial(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('RINS=');

    if (parts.length < 2) return undefined;

    const serialValue = parts[1].trim();

    const value = serialValue.replace(/\s+/g, '');

    return value.length ? value.padStart(8, '0') : undefined;
  }

  static imei(input: string) {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/IMEI=\s*([0-9]+)/);
    if (!match) return undefined;
    const imei = match[1].trim();
    return imei.length ? imei : undefined;
  }

  static iccid(input: string) {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/ICCID=\s*([0-9A-Za-z]*)/);
    if (!match) return undefined;
    const iccid = match[1].trim();
    return iccid.length ? iccid : undefined;
  }

  /*
   * @example BWS4G+LoRa_V0.0.60 (7679SA) (Oct  7 2026 12:25:15)
   * @example BWS4G_SC_V0.0.34 (MASA) — placas ainda com o firmware do BWS 4G
   */
  static firmware(input: string) {
    if (typeof input !== 'string') return undefined;
    // "+LoRa_V" (e não só "+LoRa") para não casar a versão do BLE (BWS4G+LoRa_BLE_V...)
    const match = input.match(/(BWS4G(?:_SC|\+LoRa_V)[^;\r\n]*)/);
    if (!match) return undefined;
    return match[1].trim();
  }

  /*
   * @example: www.bws.com,bws,bws
   */
  static apn(input: string): APN | undefined {
    if (typeof input !== 'string') return undefined;

    const [address, user, rawPassword] = input.split(',');
    if (!address || !user) {
      return undefined;
    }
    const password = rawPassword || undefined;
    return {
      address,
      user,
      ...(password !== undefined ? { password } : {}),
    };
  }

  /*
   * @example: IP1=161.35.12.221:5454 IP2=161.35.12.221:5454 (formato antigo)
   * @example: IP1=161.35.12.221,5454 IP2=161.35.12.221,5454 (formato BWS 4G)
   * @example: CXIP resposta: IP1=127.1.1.1,3000 IP2=127.1.1.1,3000
   */
  static ip_primary(input: string) {
    if (typeof input !== 'string') return undefined;

    const match = input.match(/IP1=(\S+)/);
    if (!match) return undefined;

    const raw = match[1];
    // Tenta separador por vírgula (BWS 4G) ou dois-pontos (formato antigo)
    const [ip, port] = raw.includes(',') ? raw.split(',') : raw.split(':');

    if (!ip || !port) return undefined;
    const portNum = Number(port);
    if (Number.isNaN(portNum)) return undefined;

    return { ip, port: portNum };
  }

  /*
   * @example: IP1=161.35.12.221:5454 IP2=161.35.12.221:5454 (formato antigo)
   * @example: IP1=161.35.12.221,5454 IP2=161.35.12.221,5454 (formato BWS 4G)
   * @example: CXIP resposta: IP1=127.1.1.1,3000 IP2=127.1.1.1,3000
   */
  static ip_secondary(input: string) {
    if (typeof input !== 'string') return undefined;

    const match = input.match(/IP2=(\S+)/);
    if (!match) return undefined;

    const raw = match[1];
    // Tenta separador por vírgula (BWS 4G) ou dois-pontos (formato antigo)
    const [ip, port] = raw.includes(',') ? raw.split(',') : raw.split(':');

    if (!ip || !port) return undefined;
    const portNum = Number(port);
    if (Number.isNaN(portNum)) return undefined;

    return { ip, port: portNum };
  }

  /**
   * Extrai o valor do dns primário de uma string que contém "IP1=161.35.12.221,5454" (BWS 4G)
   * ou "IP1=161.35.12.221:5454" (formato antigo)
   * @example: CXIP resposta: IP1=127.1.1.1,3000 IP2=127.1.1.1,3000
   *
   * @param input - A string que contém a informação do dns primário.
   * @returns O valor numérico do dns primário ou undefined se o formato não for válido.
   */
  static dns_primary(input: string) {
    if (typeof input !== 'string') return undefined;

    const matches = input.match(/IP1=([^\s]+)/g);
    if (!matches || matches.length < 1) return undefined;

    const raw = matches[0].replace(/IP1=/, '');
    // Tenta separador por vírgula (BWS 4G) ou dois-pontos (formato antigo)
    const [address, port] = raw.includes(',') ? raw.split(',') : raw.split(':');

    if (!address || !port) return undefined;

    const parsedPort = Number(port);
    if (Number.isNaN(parsedPort)) return undefined;

    return {
      address,
      port: parsedPort,
    };
  }
  /**
   * Extrai o valor do dns secundário de uma string que contém "IP2=161.35.12.221,5454" (BWS 4G)
   * ou "IP2=161.35.12.221:5454" (formato antigo)
   * @example: CXIP resposta: IP1=127.1.1.1,3000 IP2=127.1.1.1,3000
   *
   * @param input - A string que contém a informação do dns secundário.
   * @returns O valor numérico do dns secundário ou undefined se o formato não for válido.
   */
  static dns_secondary(input: string) {
    if (typeof input !== 'string') return undefined;

    const matches = input.match(/IP2=([^\s]+)/g);
    if (!matches || matches.length < 1) return undefined;

    const raw = matches[0].replace(/IP2=/, '');
    // Tenta separador por vírgula (BWS 4G) ou dois-pontos (formato antigo)
    const [address, port] = raw.includes(',') ? raw.split(',') : raw.split(':');

    if (!address || !port) return undefined;

    const parsedPort = Number(port);
    if (Number.isNaN(parsedPort)) return undefined;

    return {
      address,
      port: parsedPort,
    };
  }

  /*
   * Tipo de saída (polaridade) — vem do CHECK como string.
   * PROG DIR → 30 (Normal/MODE30)
   * PROG INV → 31 (Invertido/MODE31)
   *
   * NÃO confundir com lock_type_variation (acionamento progressivo/abrupto),
   * que vem do STATUS como número 1/2.
   */
  static lock_type(input: string): Locktype | undefined {
    if (typeof input !== 'string') return undefined;

    // Firmware antigo retorna "PROG DIR" / "PROG INV" no CHECK.
    // Firmware atual não inclui OUT_MODE no CHECK — lock_type fica indefinido e é excluído da validação.
    if (input === 'PROG DIR') return 30;
    if (input === 'PROG INV') return 31;

    return undefined;
  }

  /*
   * @example 1 ou 2 ou 3
   * tipo do bloqueio
   */
  static lock_type_variation(input: string): LocktypeVariation | undefined {
    const _input = Number(input);
    if (typeof input !== 'string' || [1, 2].every((i) => _input !== i)) return undefined;

    return Number.isNaN(_input) ? undefined : _input;
  }

  /*
   *@example 30, 180
   */
  static data_transmission_on(input: string): DataTransmission | undefined {
    if (typeof input !== 'string') return undefined;

    const [on, _] = input.split(',');
    if (!on) return undefined;
    if (Number.isNaN(on)) return undefined;
    return Number(on);
  }

  /*
   *@example 30, 180
   */
  static data_transmission_off(input: string): DataTransmission | undefined {
    if (typeof input !== 'string') return undefined;

    const [_, off] = input.split(',');
    if (!off) return undefined;
    if (Number.isNaN(off)) return undefined;
    return Number(off);
  }

  /*
   * O CHECK retorna o valor do odômetro diretamente
   * Ex: DK=50000 -> 50000 metros
   */
  static odometer(input: string): Odometer | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;

    // Retorna o valor direto sem conversão
    return Number(input);
  }

  /*
   *@example 30
   */
  static keep_alive(input: string): KeepAlive | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  /*
   *@example 30
   */
  static accelerometer_sensitivity(input: string): AccelerometerSensitivity | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  /*
   * @example 30
   */
  static lbs_position(input: string): LBSPosition | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || (input !== 'ON' && input !== 'OFF')) return undefined;
    return input === 'ON';
  }

  static cornering_position_update(input: string): CorneringPositionUpdate | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || (input !== '1' && input !== '0')) return undefined;
    return input === '1';
  }

  static led(input: string): Led | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || (input !== '1' && input !== '0')) return undefined;
    return input === '1';
  }

  static sensitivity_adjustment(input: string): SensitivityAdjustment | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  static auto_test(input: string): Bws4gLora.AutoTest | undefined {
    if (typeof input !== 'string') return undefined;
    const parts = input.split('AUTOTEST=');
    if (parts.length < 2) return undefined;
    const splited = parts[1].split(',');
    return splited.reduce((acc, cur) => {
      const [key, value] = cur.split(':');
      acc[key as keyof Bws4gLora.AutoTest] = value;
      return acc;
    }, {} as Bws4gLora.AutoTest);
  }

  /*
   * @example [LORA] VERSION=BWSIot_4G_Lora_V_0.0.3
   * @example LORA VERSION=BWSIot_4G_Lora_V_0.0.3
   */
  static lora_version(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/\bLORA\]?\s+VERSION=\s*(\S+)/i);
    return match?.[1];
  }

  /*
   * @example [BLE] VERSION=BWS4G+LoRa_BLE_V0.0.7
   * @example BLE VERSION=BWS4G+LoRa_BLE_V0.0.7
   */
  static ble_version(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/\bBLE\]?\s+VERSION=\s*(\S+)/i);
    return match?.[1];
  }

  /*
   * Decodifica os bytes recebidos pela RS232 (hex) para texto.
   * @example [RS232] RAW | 74 65 78 74 6F  →  "texto"
   */
  static rs232_raw(input: string): string | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/RAW\s*\|\s*([0-9A-Fa-f]{2}(?:\s+[0-9A-Fa-f]{2})*)/);
    if (!match) return undefined;
    return match[1]
      .trim()
      .split(/\s+/)
      .map((byte) => String.fromCharCode(Number.parseInt(byte, 16)))
      .join('');
  }

  /*
   * @example 30
   */
  static max_speed(input: string): MaxSpeed | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  static virtual_ignition_by_movement(input: string): VirtualIgnitionByMovement | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || (input !== '1' && input !== '0')) return undefined;
    return input === '1';
  }

  static communication_type(input: string): CommunicationType | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || input !== 'TCP') return undefined;
    return input;
  }

  static protocol_type(input: string): ProtocolType | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || input !== 'E3+') return undefined;
    return input;
  }

  static anti_theft(input: string): AntiTheft | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || (input !== 'OFF' && input !== 'ON')) return undefined;
    return input === 'ON';
  }

  // CHECK retorna "0" ou "1" (BWS 4G), formato antigo era "0,0" ou "1,0"
  static jammer_detection(input: string): JammerDetection | undefined {
    if (typeof input !== 'string') return undefined;

    // Formato BWS 4G: "0" ou "1"
    if (input === '0') return false;
    if (input === '1') return true;
    // Formato antigo: "0,0" ou "1,0"
    if (input === '0,0') return false;
    if (input === '1,0') return true;
    return undefined;
  }

  // CHECK retorna "0", "1" ou "2" para SDMS (Economy Mode)
  static economy_mode(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;
    const value = Number(input);
    if (Number.isNaN(value)) return undefined;
    return value;
  }

  // CHECK retorna "ON" ou "OFF" para ISHB (Ignition Status Heartbeat)
  static ignition_status_hb(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;

    if (input === 'ON') return true;
    if (input === 'OFF') return false;
    return undefined;
  }

  static angle_adjustment(input: string): AngleAdjustment | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }
  static ignition_by_voltage(input: string): IgnitionByVoltage | undefined {
    if (typeof input !== 'string') return undefined;

    const [initial, final] = input.split(',');
    if (!initial || !final) return undefined;
    if (Number.isNaN(initial) || Number.isNaN(final)) return undefined;
    return {
      initial: Number(initial),
      final: Number(final),
    };
  }

  // CHECK retorna "MASTER", "SLAVE", "UNKNOWN" ou número
  // MASTER = 1 (ignição), SLAVE = 2 (SOS), UNKNOWN = undefined
  static input_1(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    if (input === 'MASTER') return 1;
    if (input === 'SLAVE') return 2;
    if (input === 'UNKNOWN') return undefined;
    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  // CHECK retorna "MASTER", "SLAVE", "NEGATIVE", "UNKNOWN" ou número
  // MASTER = 1, SLAVE = 2, NEGATIVE = 3, UNKNOWN = undefined
  static input_2(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    if (input === 'MASTER') return 1;
    if (input === 'SLAVE') return 2;
    if (input === 'NEGATIVE') return 3;
    if (input === 'UNKNOWN') return undefined;
    if (!input || Number.isNaN(input)) return undefined;
    return Number(input);
  }

  // Resposta do GET: "GPS Speed Filter is OFF" ou "GPS Speed Filter is ON"
  static gps_filter_speed(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/GPS Speed Filter is (ON|OFF)/i);
    if (!match) return undefined;
    return match[1].toUpperCase() === 'ON';
  }

  // Resposta do GET: "GPS Periodic Wakeup is OFF" ou "GPS Periodic Wakeup is ON"
  static gps_periodic_wakeup(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/GPS Periodic Wakeup is (ON|OFF)/i);
    if (!match) return undefined;
    return match[1].toUpperCase() === 'ON';
  }

  // Resposta do GET: "Always Get AGPS is OFF" ou "Always Get AGPS is ON"
  static always_get_agps(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/Always Get AGPS is (ON|OFF)/i);
    if (!match) return undefined;
    return match[1].toUpperCase() === 'ON';
  }

  // Resposta do GET: "Ignition Type is 0" | "Ignition Type is 1" | "Ignition Type is 2"
  static ignition_type(input: string): 0 | 1 | 2 | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/Ignition Type is (\d)/i);
    if (!match) return undefined;
    const value = Number(match[1]);
    return ([0, 1, 2] as const).find((v) => v === value);
  }

  // Resposta do GET: "NETWORKMODE=AUTO" | "NETWORKMODE=4G" | "NETWORKMODE=2G"
  // Firmware mais recente retorna "NetworkMode: LTE" | "NetworkMode: AUTO" | "NetworkMode: GSM"
  static network_mode(input: string): 'AUTO' | '4G' | '2G' | undefined {
    if (typeof input !== 'string') return undefined;

    // Formato antigo: NETWORKMODE=4G
    const matchEq = input.match(/NETWORKMODE=(\w+)/i);
    if (matchEq) {
      const value = matchEq[1].toUpperCase();
      return (['AUTO', '4G', '2G'] as const).find((v) => v === value);
    }

    // Formato novo: "NetworkMode: LTE" | "NetworkMode: AUTO" | "NetworkMode: GSM" | "NetworkMode: GSM+LTE"
    // (GSM+LTE pode aparecer com qualquer ordem ou separador — captura tudo até ; ou whitespace longo)
    const matchColon = input.match(/NetworkMode:\s*([A-Z0-9+]+)/i);
    if (matchColon) {
      const value = matchColon[1].toUpperCase();
      // GSM+LTE ou LTE+GSM = modo dual = AUTO
      if (value.includes('GSM') && value.includes('LTE')) return 'AUTO';
      if (value === 'LTE') return '4G';
      if (value === 'GSM') return '2G';
      if (value === 'AUTO') return 'AUTO';
    }

    return undefined;
  }

  // Resposta do GET: "GPS Replay Fix Sleep is ON" ou "GPS Replay Fix Sleep is OFF"
  static gps_replay_fix_sleep(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/GPS Replay Fix Sleep is (ON|OFF)/i);
    if (!match) return undefined;
    return match[1].toUpperCase() === 'ON';
  }

  // Resposta do GET: "Improper Movement is ON" ou "Improper Movement is OFF"
  static improper_movement(input: string): boolean | undefined {
    if (typeof input !== 'string') return undefined;
    const match = input.match(/Improper Movement is (ON|OFF)/i);
    if (!match) return undefined;
    return match[1].toUpperCase() === 'ON';
  }

  // NOTA: HR no STATUS mostra valor acumulado atual (0 = não definido/zerado)
  // O comando HRn define o valor INICIAL, não é retornado no CHECK
  static horimeter(input: string): number | undefined {
    if (typeof input !== 'string') return undefined;

    if (!input || Number.isNaN(input)) return undefined;
    const value = Number(input);
    // Retorna o valor mesmo se for 0, pois pode estar configurado como 0
    return value;
  }

  static methods() {
    return {
      check: Bws4gLoraParser.check,
      status: Bws4gLoraParser.status,
      serial: Bws4gLoraParser.serial,
      imei: Bws4gLoraParser.imei,
      iccid: Bws4gLoraParser.iccid,
      firmware: Bws4gLoraParser.firmware,
      apn: Bws4gLoraParser.apn,
      ip_primary: Bws4gLoraParser.ip_primary,
      ip_secondary: Bws4gLoraParser.ip_secondary,
      dns_primary: Bws4gLoraParser.dns_primary,
      dns_secondary: Bws4gLoraParser.dns_secondary,
      lock_type: Bws4gLoraParser.lock_type,
      lock_type_variation: Bws4gLoraParser.lock_type_variation,
      data_transmission_on: Bws4gLoraParser.data_transmission_on,
      data_transmission_off: Bws4gLoraParser.data_transmission_off,
      odometer: Bws4gLoraParser.odometer,
      keep_alive: Bws4gLoraParser.keep_alive,
      accelerometer_sensitivity: Bws4gLoraParser.accelerometer_sensitivity,
      lbs_position: Bws4gLoraParser.lbs_position,
      cornering_position_update: Bws4gLoraParser.cornering_position_update,
      led: Bws4gLoraParser.led,
      sensitivity_adjustment: Bws4gLoraParser.sensitivity_adjustment,
      auto_test: Bws4gLoraParser.auto_test,
      max_speed: Bws4gLoraParser.max_speed,
      virtual_ignition_by_movement: Bws4gLoraParser.virtual_ignition_by_movement,
      communication_type: Bws4gLoraParser.communication_type,
      protocol_type: Bws4gLoraParser.protocol_type,
      anti_theft: Bws4gLoraParser.anti_theft,
      jammer_detection: Bws4gLoraParser.jammer_detection,
      ignition_status_hb: Bws4gLoraParser.ignition_status_hb,
      angle_adjustment: Bws4gLoraParser.angle_adjustment,
      ignition_by_voltage: Bws4gLoraParser.ignition_by_voltage,
      input_1: Bws4gLoraParser.input_1,
      input_2: Bws4gLoraParser.input_2,
      horimeter: Bws4gLoraParser.horimeter,
      gps_filter_speed: Bws4gLoraParser.gps_filter_speed,
      gps_periodic_wakeup: Bws4gLoraParser.gps_periodic_wakeup,
      always_get_agps: Bws4gLoraParser.always_get_agps,
      ignition_type: Bws4gLoraParser.ignition_type,
      network_mode: Bws4gLoraParser.network_mode,
      gps_replay_fix_sleep: Bws4gLoraParser.gps_replay_fix_sleep,
      improper_movement: Bws4gLoraParser.improper_movement,
    };
  }

  static parser<K extends keyof ReturnType<typeof Bws4gLoraParser.methods>>(
    input: string[],
    ...keys: K[]
  ): { [P in K]?: ReturnType<ReturnType<typeof Bws4gLoraParser.methods>[P]> } {
    const table = Bws4gLoraParser.methods();
    const out = {} as {
      [P in K]?: ReturnType<ReturnType<typeof Bws4gLoraParser.methods>[P]>;
    };

    // O firmware do BWS 4G responde comandos em batch ("*N;CMD1;CMD2;...") numa ÚNICA
    // linha terminada por um só \n, com os blocos internos separados por ';'. O readReader
    // quebra por \r?\n, então essa resposta chega como uma linha só. Para que parsers que
    // esperam um trecho isolado (ip_secondary, check, GETs) funcionem, expandimos cada linha
    // em candidatos: a própria linha + cada segmento separado por ';'.
    //
    // A linha original é mantida porque alguns parsers (ex.: status) fazem o próprio split por
    // ';' e precisam de todos os pares juntos. O find() pega o primeiro resultado válido, então
    // cada parser acha o candidato onde casa (status → linha inteira; ip_secondary → segmento).
    const candidates = input.flatMap((line) => {
      if (typeof line !== 'string' || !line.includes(';')) return [line];
      const segments = line.split(';').map((s) => s.trim());
      return [line, ...segments];
    });

    for (const key of keys) {
      const fn = table[key as keyof typeof table] as (line: string) => any; // cada parser: (string) => T | undefined
      // chame a função explicitamente; se quiser garantir 'this', use call
      out[key] = candidates
        .map((line) => fn.call(Bws4gLoraParser, line))
        .find((v) => v !== undefined);
    }
    return out;
  }
}

// const check = "Sim=VAZIO SOS=PANIC APN=bws.br,bws,bws TZ=W0 HB=60,7200 MG=0 TX=300 BJ=0 ACCMODE=1 TDET=20 WKMODE=0 DD=0 OD=120 SDMS=2 TUR=1 PROT_COM=TCP DK=0 JD=1 LBS=ON OUT_MODE=2 LED=1 IV=1 ACC=1 GPRS=2G GPS=V PROT=E3+ DC=1500,8000 Voltage=13.50,12.50 AF=OFF GS=120 ACK=30 IN2_MODE=1 MQ=OFF

// const status = "BATTERY EXTERNAL:11.49V;BATT_INT:0%;ACC:ON;GPRS:Ok;GPS:0;GSM:20;HR: ;Buffer Memory:0;Tech:4G E_UTRAN;IP:143.198.247.1;Port:2000;ENGINE MODE1"

// const autotest SN:869671070546377,IC:89551805400523770076,FW:BWSiot_E3+4GW_V1.51 (DIV=100k+68k) (EG915U-LA) (Aug  9 2024 10:13:13),GPS:OK,GPSf:NOK,GSM:OK,LTE:OK,IN1:OK,IN2:NOK,OUT:NOK,ACELC:MC3632,ACELP:1,VCC:OK,CHARGER:OK,ID_ACEL:71,ID_MEM:C22536:BATT_VOLT:5.61V
